import React from 'react'
import type { ChatMessage } from '../../types'
import { formatMessageTime } from '@/utils/date-formatters'
import { TimeDisplay } from '@/components/common/TimeDisplay'
import { MessageActionsMenu } from '../MessageActionsMenu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ArrowUpRight } from 'lucide-react'

interface SystemMessageProps {
  message: ChatMessage
  threadId?: string
  companyId?: string
  sessionId?: string
  onSessionDetailsClick?: (sessionId: string) => void
}

/** Display properties for rendering links/tooltips in system messages */
interface DisplayProps {
  linkUrl?: string
  linkText?: string
  tooltipText?: string
  contentBeforeLink?: string
  contentAfterLink?: string
}

export const SystemMessage: React.FC<SystemMessageProps> = ({
  message,
  threadId,
  companyId,
  sessionId,
  onSessionDetailsClick,
}) => {
  const IconComponent = message.icon

  // Check if message has display properties for custom rendering
  const display = (message.data as { _display?: DisplayProps } | undefined)
    ?._display

  const hasLinkDisplay = display?.linkText

  return (
    <div className="my-2 flex justify-center group relative">
      <div className="text-gray-500 dark:text-muted-foreground text-xs flex items-center gap-2 mb-3 relative group/card">
        {threadId && (
          <div className="absolute -right-14 z-30">
            <MessageActionsMenu
              threadId={threadId}
              messageId={message.id}
              companyId={companyId}
              sessionId={sessionId}
              onSessionDetailsClick={onSessionDetailsClick}
              layout="row"
              tooltipSide="top"
            />
          </div>
        )}
        {IconComponent && <IconComponent className="w-3 h-3" />}
        {hasLinkDisplay ? (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default">
                  {display.contentBeforeLink}
                  {display.linkUrl ? (
                    <a
                      href={display.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-gray-700 dark:hover:text-foreground"
                      onClick={e => e.stopPropagation()}
                    >
                      {display.linkText}
                      <ArrowUpRight className="w-3 h-3 inline" />
                    </a>
                  ) : (
                    <span className="cursor-default">{display.linkText}</span>
                  )}
                  {display.contentAfterLink}
                </span>
              </TooltipTrigger>
              {display.tooltipText && (
                <TooltipContent side="top" className="text-xs">
                  {display.tooltipText}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span>{message.content}</span>
        )}
        <span className="text-gray-400 dark:text-muted-foreground/60">
          •{' '}
          <TimeDisplay
            date={message.createdAt}
            formatter={formatMessageTime}
            className="inline"
          />
        </span>
      </div>
    </div>
  )
}
